import type { VoiceAnalyticsTracker } from "@/store/audio-capture/analytics/voice-analytics-tracker";
import type { AppStoreAudioCaptureStore } from "@/store/audio-capture/AppStoreAudioCaptureStore";

import type { RecorderEngine } from "./base-recorder-engine";
import { DesktopRecorderOrchestrator } from "@/store/audio-capture/desktop/capture/desktop-recorder-orchestrator";

export class DesktopRecorderEngine implements RecorderEngine {
  private orchestrator: DesktopRecorderOrchestrator | null = null;

  constructor(
    private readonly store: AppStoreAudioCaptureStore,
    private readonly analytics: VoiceAnalyticsTracker
  ) {}

  async start(noteId: string): Promise<void> {
    if (!this.orchestrator) {
      this.orchestrator = new DesktopRecorderOrchestrator(this.store);
    }
    await this.orchestrator.start(noteId);
    this.analytics.sessionStart(this.analyticsPayload());
  }

  async pause(): Promise<boolean> {
    const ok = await this.orchestrator?.pause();
    if (ok) {
      this.analytics.sessionPause(this.analyticsPayload());
      return true;
    }
    return false;
  }

  async resume(): Promise<boolean> {
    const ok = await this.orchestrator?.resume();
    if (ok) {
      this.analytics.sessionResume(this.analyticsPayload());
      return true;
    }
    return false;
  }

  async stop(): Promise<void> {
    if (this.orchestrator) {
      await this.orchestrator.stop();
      this.analytics.sessionDone(this.analyticsPayload());
    }
  }

  async dispose(): Promise<void> {
    await this.orchestrator?.dispose();
    this.orchestrator = null;
  }

  private analyticsPayload() {
    return {
      sessionId: this.store.sessionId,
      capturedAudioId: this.store.capturedAudioId,
      noteId: this.store.activeNoteId,
    };
  }
}
